// Executive Summary — API layer (live only).
//
// The stage-level counts come from njm-corda-pcmp `/executiveSummary/summary`,
// which does the counting in SQL. The dashboard's /fetchProjectDashboard could
// have produced the same numbers, but it ships every batch of every project to
// do it — far too much payload for a portfolio-wide roll-up.
//
// The CMO and Site / MFG Plant dropdowns need no new endpoint: they reuse the
// dashboard filter bar's own /fetchCMOMembers and /fetchMfgPlants, so the three
// screens always offer the same option lists.
//
// On a missing base URL or a failed call every function resolves to an empty
// envelope of the same shape, so the screen renders its empty state rather than
// throwing.

import axios from 'axios';
import { appUrl } from '../../../../../../utils/config';
import { getApiBaseUrl } from '../../CommonMethods/CommonMethods';
import { fetchCMOMembersAPI, fetchMfgPlantsAPI } from '../Dashboard/filtersAPI';

const NS = 'executiveSummary';
const post = (op, payload) => {
  const base = getApiBaseUrl(appUrl);
  if (!base) return Promise.reject(new Error('API base URL not available'));
  return axios.post(`${base}/${NS}/${op}`, payload || {});
};

/**
 * CMO options — [{ id, label }] where `id` is the organization_id sent back as
 * cdmoOrgIDs. Same source and de-duplication as the dashboard's filter bar.
 */
export const fetchExecCmoOptionsAPI = async () => {
  try {
    const res = await fetchCMOMembersAPI();
    const seen = new Set();
    const options = [];
    (res?.data || []).forEach((m) => {
      if (!m || m.organization_id == null) return;
      const id = String(m.organization_id);
      if (seen.has(id)) return;
      seen.add(id);
      options.push({ id, label: m.organization_name || id });
    });
    return { status: true, data: options };
  } catch (error) {
    console.error('[ExecutiveSummary] Failed to load CMO options:', error);
    return { status: false, data: [] };
  }
};

/**
 * Site / MFG Plant options, narrowed to the chosen CMOs — [{ id, label }].
 * `id` is the SITE NAME (what the summary filters on); the label pairs it with
 * the plant as "HYD / Unit II", exactly the form the slides show.
 */
export const fetchExecSiteOptionsAPI = async (cmoOrgIds = []) => {
  try {
    const res = await fetchMfgPlantsAPI();
    const wanted = cmoOrgIds.length ? new Set(cmoOrgIds.map(String)) : null;
    const seen = new Set();
    const options = [];
    (res?.data || []).forEach((p) => {
      const site = (p?.SiteName != null ? String(p.SiteName) : '').trim();
      const plant = (p?.mfgPlantShortName != null ? String(p.mfgPlantShortName) : '').trim();
      const name = site || plant;
      if (!name) return;
      if (wanted && !wanted.has(String(p.vendorMosymphonyID))) return;
      if (seen.has(name)) return;
      seen.add(name);
      options.push({ id: name, label: plant && plant !== name ? `${name} / ${plant}` : name });
    });
    return { status: true, data: options };
  } catch (error) {
    console.error('[ExecutiveSummary] Failed to load site options:', error);
    return { status: false, data: [] };
  }
};

/**
 * Project ID options for the chosen CMOs / sites — [{ id, label }]. `id` is the
 * collabrixProjectID; the label is the dashboard's composed Project ID
 * (productCode-sponsorProjectID-cmoProductCode).
 */
export const fetchExecProjectOptionsAPI = async ({ cdmoOrgIDs = [], cmoSites = [] } = {}) => {
  try {
    const res = await post('projects', { cdmoOrgIDs, cmoSites });
    const data = res?.data?.status ? res.data.data : null;
    return { status: !!data, data: Array.isArray(data) ? data : [] };
  } catch (error) {
    console.error('[ExecutiveSummary] Failed to load project options:', error);
    return { status: false, data: [] };
  }
};

/**
 * The summary itself → { lastSyncedAt, ongoing[], past[] }, each entry a
 * campaign block carrying its stage rows with planned/completed/ongoing counts.
 */
export const fetchExecutiveSummaryAPI = async ({
  cdmoOrgIDs = [],
  cmoSites = [],
  collabrixProjectIDs = [],
} = {}) => {
  try {
    const res = await post('summary', { cdmoOrgIDs, cmoSites, collabrixProjectIDs });
    const data = res?.data?.status ? res.data.data : null;
    if (!data) return { status: false, data: { lastSyncedAt: null, ongoing: [], past: [] } };
    return {
      status: true,
      data: {
        lastSyncedAt: data.lastSyncedAt || null,
        ongoing: Array.isArray(data.ongoing) ? data.ongoing : [],
        past: Array.isArray(data.past) ? data.past : [],
      },
    };
  } catch (error) {
    console.error('[ExecutiveSummary] Failed to load summary:', error);
    return { status: false, data: { lastSyncedAt: null, ongoing: [], past: [] } };
  }
};
